import { useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { driver, type Driver, type Popover } from 'driver.js';
import { useNavigate } from 'react-router-dom';
import 'driver.js/dist/driver.css';
import '@/styles/tour.css';
import { useAuthStore } from '@/stores/useAuthStore';
import { prefersReducedMotion } from '@/theme/reducedMotion';
import { tourService } from '@/services/tourService';
import { TourContext } from './tour-context';
import { stepsFor, tourRoleFor, visibleTourTarget, type TourRole, type TourStep } from './tours';

interface TourSession {
  finish: () => void;
}

/** How long a page change may take before a missing stop is skipped. */
const PAGE_WAIT_MS = 4000;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Waits until the step's page is the current one and its target is on screen.
 * A hidden duplicate (mobile search, collapsed sidebar) does not count.
 */
async function waitForTarget(step: TourStep, isCurrent: () => boolean): Promise<HTMLElement | null> {
  const started = Date.now();
  while (isCurrent() && Date.now() - started < PAGE_WAIT_MS) {
    if (!step.path || window.location.pathname === step.path) {
      const element = visibleTourTarget(step.element);
      if (element) return element;
    }
    await wait(50);
  }
  return null;
}

export function TourProvider({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const sessionRef = useRef<TourSession | null>(null);

  const startTour = useCallback(
    (role: TourRole) => {
      const ruolo = useAuthStore.getState().user?.ruolo;
      const defs = stepsFor(ruolo ?? (role === 'cliente' ? 'cliente_admin' : 'admin'));
      if (defs.length === 0) return;

      tourService.noteStarted();
      const reduced = prefersReducedMotion();
      const instance: { current: Driver | null } = { current: null };
      let finished = false;
      let token = 0;

      const finish = (): void => {
        if (finished) return;
        finished = true;
        token += 1;
        void tourService.markCompleted(ruolo ? tourRoleFor(ruolo) : role);
        instance.current?.destroy();
        if (sessionRef.current?.finish === finish) sessionRef.current = null;
      };

      const show = (index: number, direction: 1 | -1): void => {
        const mine = ++token;
        const step = defs[index];
        if (!step) {
          if (direction === 1) finish();
          return;
        }
        if (step.path && window.location.pathname !== step.path) {
          void navigate(step.path);
        }

        void waitForTarget(step, () => mine === token && !finished).then((element) => {
          if (mine !== token || finished) return;
          if (!element) {
            const next = index + direction;
            if (next < 0 || next >= defs.length) {
              if (direction === 1) finish();
              return;
            }
            show(next, direction);
            return;
          }

          const last = index === defs.length - 1;
          const popover: Popover = {
            title: step.title,
            description: step.description,
            side: step.side,
            align: step.align,
            showButtons: ['next', 'previous', 'close'],
            showProgress: true,
            progressText: `${index + 1} di ${defs.length}`,
            nextBtnText: last ? 'Fine' : 'Avanti',
            prevBtnText: 'Indietro',
            disableButtons: index === 0 ? ['previous'] : [],
            onNextClick: () => {
              if (last) finish();
              else show(index + 1, 1);
            },
            onPrevClick: () => {
              if (index > 0) show(index - 1, -1);
            },
            onCloseClick: () => {
              finish();
            },
          };

          instance.current?.highlight({ element, popover });
        });
      };

      sessionRef.current?.finish();

      instance.current = driver({
        allowClose: true,
        overlayColor: '#0A1F44',
        overlayOpacity: 0.65,
        stagePadding: 8,
        stageRadius: 8,
        smoothScroll: !reduced,
        animate: !reduced,
        duration: reduced ? 0 : 250,
        popoverClass: 'wenkey-tour-popover',
        onPopoverRender: (popover) => {
          if (popover.footerButtons.querySelector('.wenkey-tour-skip-btn')) return;
          const skip = document.createElement('button');
          skip.type = 'button';
          skip.className = 'wenkey-tour-skip-btn driver-popover-footer-btn';
          skip.textContent = 'Salta';
          skip.addEventListener('click', finish);
          popover.footerButtons.prepend(skip);
        },
        onDestroyStarted: () => {
          finish();
        },
      });

      sessionRef.current = { finish };
      show(0, 1);
    },
    [navigate],
  );

  const stopTour = useCallback(() => {
    sessionRef.current?.finish();
  }, []);

  const value = useMemo(() => ({ startTour, stopTour }), [startTour, stopTour]);

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
